import React, { Suspense, lazy, useContext, useEffect } from 'react';
import { Route, Switch, BrowserRouter, Redirect, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, AuthContext } from './utils/auth.js';
import { initializeTagManager } from './utils/gtm.js';
import { loadTawkTo } from './utils/tawkTo.js';
import { isAdminEmail } from './utils/adminUsers.js';
import { DevSidebarProvider } from './context/DevSidebarContext.js';
import { ImpersonationProvider } from './context/ImpersonationContext.js';
import AdminDevToolbar from './components/AdminDevToolbar.js';
import AdminDevToolsSync from './components/AdminDevToolsSync.js';
import AppShell from './components/layout/AppShell.js';
import { PageSpinner } from './ui/Spinner.js';

// Auth screens are small and needed first: load them eagerly.
import Login from './screens/Login.js';
import Register from './screens/Register.js';
import ForgotPassword from './screens/ForgotPassword.js';
import EmailVerificationNotice from './screens/VerifyEmail.js';

// Everything behind authentication is code-split per route.
const Dashboard = lazy(() => import('./screens/Dashboard.js'));
const DataExtraction = lazy(() => import('./screens/DataExtraction.js'));
const DocumentClassification = lazy(() => import('./screens/DocumentClassification.js'));
const Account = lazy(() => import('./screens/Account.js'));
const Api = lazy(() => import('./screens/Api.js'));
const Billing = lazy(() => import('./screens/Billing.js'));
const Admin = lazy(() => import('./screens/Admin.js'));
const ServerHealth = lazy(() => import('./screens/Admin/ServerHealth.js'));
const SubscriptionIframe = lazy(() => import('./SubscriptionIframe.js'));
const DemoIframe = lazy(() => import('./DemoIframe.js'));
// Development only: living style guide at /__design (tree-shaken from production builds).
const DesignKit = process.env.NODE_ENV === 'development' ? lazy(() => import('./dev/DesignKit.js')) : null;

const APP_ROUTES = ['/dashboard', '/extractions', '/classifications', '/account', '/api', '/billing'];

const toastOptions = {
  duration: 3500,
  className: 'ex-toast',
  success: { iconTheme: { primary: '#059669', secondary: '#ffffff' } },
  error: { iconTheme: { primary: '#dc2626', secondary: '#ffffff' } },
  loading: { iconTheme: { primary: '#4f46e5', secondary: '#e0e7ff' } },
};

function EnvironmentBadge({ text }) {
  return <div className="rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-pop">{text}</div>;
}

function FullPageFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <PageSpinner />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

export default function App() {
  const environment = process.env.REACT_APP_ENV;

  useEffect(() => {
    initializeTagManager();
    loadTawkTo();
  }, []);

  if (environment === 'pricing') {
    return (
      <Suspense fallback={<FullPageFallback />}>
        <SubscriptionIframe />
      </Suspense>
    );
  }

  if (environment === 'demo') {
    return (
      <Suspense fallback={<FullPageFallback />}>
        <DemoIframe />
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <DevSidebarProvider>
         <ImpersonationProvider>
          <AdminDevToolsSync />
          <ScrollToTop />
          <Toaster position="top-right" gutter={8} toastOptions={toastOptions} containerStyle={{ top: 16, right: 16 }} />

          <Switch>
            <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
            <Route exact path="/login" component={Login} />
            <Route exact path="/forgot" component={ForgotPassword} />
            <Route exact path="/register" component={Register} />
            <VerifyEmailRoute exact path="/verify-email" component={EmailVerificationNotice} />

            {DesignKit && (
              <Route exact path="/__design">
                <DesignKitRoute />
              </Route>
            )}

            <AdminRoute exact path="/admin/health" component={ServerHealth} />
            <AdminRoute exact path="/admin/:section?" component={Admin} />

            <PrivateRoute path={APP_ROUTES}>
              <AppShell>
                <Suspense fallback={<PageSpinner />}>
                  <Switch>
                    <Route exact path="/dashboard" component={Dashboard} />
                    <Route path="/extractions" component={DataExtraction} />
                    <Route path="/classifications" component={DocumentClassification} />
                    <Route exact path="/account" component={Account} />
                    <Route exact path="/api" component={Api} />
                    <Route exact path="/billing" component={Billing} />
                  </Switch>
                </Suspense>
              </AppShell>
            </PrivateRoute>

            <Route path="*" render={() => <Redirect to="/dashboard" />} />
          </Switch>

          {environment === 'test' && (
            <div className="fixed bottom-4 right-4 z-50">
              <EnvironmentBadge text="test app" />
            </div>
          )}
          {environment !== 'production' && <AdminDevToolbar />}
         </ImpersonationProvider>
        </DevSidebarProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

/** Development only: renders the design kit inside the shell with a stub user. */
function DesignKitRoute() {
  const { currentUser } = useContext(AuthContext);
  const stubUser = currentUser || { uid: 'design-kit', email: 'design@extracta.ai', displayName: 'Design Kit', emailVerified: true, providerData: [] };
  return (
    <AuthContext.Provider value={{ currentUser: stubUser, isEmailVerified: true, refreshUser: async () => stubUser, authTick: 0 }}>
      <AppShell title="Design kit">
        <Suspense fallback={<PageSpinner />}>
          <DesignKit />
        </Suspense>
      </AppShell>
    </AuthContext.Provider>
  );
}

/** Remembers where an unauthenticated visitor wanted to go. */
function rememberIntendedPath(location) {
  try {
    const fullPath = location.pathname + (location.search || '');
    if (fullPath && fullPath !== '/login') localStorage.setItem('intendedPath', fullPath);
  } catch (error) {
    // storage unavailable
  }
}

/** Authenticated + verified users only. Children or `component` are rendered. */
function PrivateRoute({ component: RouteComponent, children, ...rest }) {
  const { currentUser, isEmailVerified } = useContext(AuthContext);

  return (
    <Route
      {...rest}
      render={(routeProps) => {
        if (!currentUser) {
          rememberIntendedPath(routeProps.location);
          return <Redirect to="/login" />;
        }
        if (!isEmailVerified) {
          return <Redirect to="/verify-email" />;
        }
        if (RouteComponent) return <RouteComponent {...routeProps} />;
        return children;
      }}
    />
  );
}

/** Only for signed-in users whose email is not verified yet. */
function VerifyEmailRoute({ component: RouteComponent, ...rest }) {
  const { currentUser, isEmailVerified } = useContext(AuthContext);

  return (
    <Route
      {...rest}
      render={(routeProps) => {
        if (!currentUser) {
          rememberIntendedPath(routeProps.location);
          return <Redirect to="/login" />;
        }
        if (isEmailVerified) return <Redirect to="/dashboard" />;
        return <RouteComponent {...routeProps} />;
      }}
    />
  );
}

/** Admin only. Non-admins are sent to the dashboard. */
function AdminRoute({ component: Component, ...rest }) {
  const { currentUser } = useContext(AuthContext);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!currentUser) {
          rememberIntendedPath(props.location);
          return <Redirect to="/login" />;
        }
        if (!isAdminEmail(currentUser.email)) {
          return <Redirect to="/dashboard" />;
        }
        return (
          <Suspense fallback={<FullPageFallback />}>
            <Component {...props} />
          </Suspense>
        );
      }}
    />
  );
}
